import { useMemo } from "react";
import { numberFormat } from "../utils/numberFormat";
import OrderItem from "../types/OrderItem";

interface TotalsResult {
  medicamentos: string;
  perfumaria: string;
  kit: string;
  liquid: string;
  total: string;
  liquidNumber: number;
  minimumReached?: boolean | undefined;
}

/**
 * Hook para calcular totais do pedido
 * @param items - Array de itens do pedido
 * @param vlrMinimo - Valor mínimo do pedido (opcional, usado para calcular minimumReached)
 * @returns Objeto com totais formatados e numéricos
 */
export const useTotals = (
  items: OrderItem[],
  vlrMinimo?: number
): TotalsResult => {
  return useMemo(() => {
    if (items.length === 0) {
      return {
        medicamentos: "R$ 0,00",
        perfumaria: "R$ 0,00",
        kit: "R$ 0,00",
        liquid: "R$ 0,00",
        total: "R$ 0,00",
        liquidNumber: 0,
        minimumReached: vlrMinimo ? false : undefined,
      };
    }

    // Calcula total usando vlr_total do item
    const total = items.reduce((acc, current) => {
      const vlrTotal = current.vlr_total || 0;
      const qtPedida = current.qt_pedida || 0;
      return acc + parseFloat(String(vlrTotal)) * qtPedida;
    }, 0);

    // Calcula medicamentos (id_tipo_mercadoria === "1" e id_falta === "N")
    const medicamentosNumber = items.reduce((acc, current) => {
      const isMedicamento = current.mercadoria?.id_tipo_mercadoria === "1";
      const semFalta = current.mercadoria?.infos?.id_falta === "N";
      if (isMedicamento && semFalta) {
        const vlrTotal = current.vlr_total || 0;
        const qtPedida = current.qt_pedida || 0;
        return acc + parseFloat(String(vlrTotal)) * qtPedida;
      }
      return acc;
    }, 0);

    // Calcula perfumaria (id_tipo_mercadoria === "2" e id_falta === "N")
    const perfumariaNumber = items.reduce((acc, current) => {
      const isPerfumaria = current.mercadoria?.id_tipo_mercadoria === "2";
      const semFalta = current.mercadoria?.infos?.id_falta === "N";
      if (isPerfumaria && semFalta) {
        const vlrTotal = current.vlr_total || 0;
        const qtPedida = current.qt_pedida || 0;
        return acc + parseFloat(String(vlrTotal)) * qtPedida;
      }
      return acc;
    }, 0);

    // Calcula kit (id_tipo_mercadoria === "-1" e id_falta === "N")
    const kitNumber = items.reduce((acc, current) => {
      const isKit = current.mercadoria?.id_tipo_mercadoria === "-1";
      const semFalta = current.mercadoria?.infos?.id_falta === "N";
      if (isKit && semFalta) {
        const vlrTotal = current.vlr_total || 0;
        const qtPedida = current.qt_pedida || 0;
        return acc + parseFloat(String(vlrTotal)) * qtPedida;
      }
      return acc;
    }, 0);

    // Calcula líquido usando vlr_preco do item
    const liquidNumber = items.reduce((acc, current) => {
      const semFalta = current.mercadoria?.infos?.id_falta === "N";
      if (semFalta) {
        const vlrPreco = current.vlr_preco || 0;
        const qtPedida = current.qt_pedida || 0;
        return acc + parseFloat(String(vlrPreco)) * qtPedida;
      }
      return acc;
    }, 0);

    // Calcula minimumReached se vlrMinimo foi fornecido
    const minimumReached =
      vlrMinimo !== undefined
        ? liquidNumber >= parseFloat(String(vlrMinimo))
        : undefined;

    return {
      medicamentos: numberFormat(medicamentosNumber),
      perfumaria: numberFormat(perfumariaNumber),
      kit: numberFormat(kitNumber),
      liquid: numberFormat(liquidNumber),
      total: numberFormat(total),
      liquidNumber, // Mantém o valor numérico para cálculo de minimumReached e parcelamento
      minimumReached,
    };
  }, [items, vlrMinimo]);
};
